import { timeDay, timeMinute } from 'd3-time';
import { internalUnits, mmolL, mgdL } from './consts';
import { parseDate, urlDateFormat } from './formats';
import { merge } from 'd3-array';

export const glucoseMolarMass = 18.01559;

export const idKey = d => d.id || d;
export const idOrTimeKey = d => d.id || d.time;
export const nameKey = d => d.name || d;
export const dayKey = d => urlDateFormat(new Date(d.time));

export function guessGlucoseUnit(value) {
  if (value >= 0 && value < 35) {
    return mmolL;
  }
  if (value >= 35 && value < (35 * glucoseMolarMass)) {
    return mgdL;
  }
  throw new Error(`Cannot guess the units for glucose value ${value}`);
};

function getFromPossibleValues(d) {
  if (!isNaN(d.sgv)) {
    return d.svg;
  }
  if (!isNaN(d.smbg)) {
    return d.smbg;
  }
  if (!isNaN(d.glucose)) {
    return d.glucose;
  }
  if (!isNaN(d.value)) {
    return d.value;
  }
  return d;
}

export function getProfilesData(days, basal, profiles) {
  const realBasal = days.reduce((o, d) => {
    if (basal[d]) {
      o[d] = [...(basal[d] || [])];
    }
    return o;
  }, {});

  const basalEntries = merge(Object.values(realBasal));

  const lastDay = days[days.length - 1];
  const startDate = parseDate(days[0]); 
  const endDate = timeDay.offset(parseDate(lastDay), 1);
  let profileUseTimes = {};
  profiles && Object.entries(profiles).forEach(p => {
    profileUseTimes[p[0]] = 0;
  })
  const profileChanges = basalEntries.reduce((arr, e, i, a) => {
    if (e.profile !== arr[arr.length - 1]) {
      arr.push(e.profile);
    } else if (i > 0) {
      if (!profileUseTimes[e.profile]) {
        profileUseTimes[e.profile] = 0;
      }
      let start = new Date(a[i - 1].time);
      if (start < startDate) {
        start = startDate;
      }
      let end = new Date(e.time);
      if (end > endDate) {
        end = endDate;
      }
      profileUseTimes[e.profile] += Math.max(timeMinute.count(start, end), 0);
    }
    return arr;
  }, [(basalEntries[0] || {}).profile]);
  const totalMinutes =  timeMinute.count(startDate, endDate);
  // Only count changes...
  profileChanges.shift();
  return ({
    'profileChanges':profileChanges,
    'totalMinutes':totalMinutes,
    'profileUseTimes': profileUseTimes
  })
}

export function glucoseAccessor(d) {
  const glucoseValue = getFromPossibleValues(d);
  const unit = d.unit ? d.unit.toLowerCase() : guessGlucoseUnit(glucoseValue);
  if (unit === internalUnits) {
    return glucoseValue;
  }
  if (unit === mmolL && internalUnits === mgdL) {
    return glucoseValue * glucoseMolarMass;
  }
  if (unit === mgdL && internalUnits === mmolL) {
    return glucoseValue / glucoseMolarMass;
  }
  throw new Error(`Cannot convert value ${glucoseValue} from ${unit} to ${internalUnits}`);
};

export function timeAccessor(d) {
  if (!d.time) {
    console.warn('Invalid use of timeAccessor', d);
    return d;
  }
  if (d.time.getTime) {
    return d.time;
  }
  return new Date(d.time);
}

export function timeSort(a, b) {
  return timeAccessor(a).getTime() - timeAccessor(b).getTime();
}

